"use client";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { TGenerateLewkboardsRequest } from "../../../b2c-api/src/routers/lewkboards";
import FormOccassionStep from "./steps/f-s-occassion";
import CircleProgress from "../ui/progress-circle";
import { FormSteps } from "@/lib/types/global-types";
import { Button } from "../ui/button";
import {
  Camera,
  ChevronLeftIcon,
  ChevronRightIcon,
  GemIcon,
} from "lucide-react";
import FormItemStep from "./steps/f-s-items";
import LewkLoader from "../c-loader";
import { LewkAPI } from "@/config/api/api-client";
import { Routes_Dashboard } from "@/config/page-routes";
import shopLoader from "../../public/assets/loaders/shop.json";
import {
  NetworkError,
  ClientError,
  ClientStatusCodes,
} from "@/lib/utils/error-definitions";
import { useRouter } from "@/lib/utils/router-events";
import dynamic from "next/dynamic";
import { createClient } from "@/lib/utils/supabase/client";
import { useVisitor } from "@/lib/store/visitor-store/store";
import { Label } from "../ui/label";

const Lottie = dynamic(() => import("lottie-react"), { ssr: false });

const LewkboardForm = () => {
  // ---------------------------- HOOKS ---------------------------- //
  const GlobalLewkboardForm = useForm<TGenerateLewkboardsRequest>();
  const router = useRouter();
  const { incrementUsageCount } = useVisitor();

  //TEMP - FOR FILE UPLOAD - UNTIL NEW DESIGN
  const inputRef = useRef<HTMLInputElement>(null);

  // We will temp check auth until we split the forms into separate components for authed and unauthed users
  const supabase = createClient();

  // ---------------------------- CONST ---------------------------- //
  const FORM_STEP_ARRAY = Object.values(FormSteps);

  // ---------------------------- STATES ---------------------------- //

  // Stores the current step in the form
  const [currentFormStep, setCurrentFormStep] = useState<FormSteps>(
    FormSteps.OCCASION
  );

  // Stores the progress percentage of the form
  const [formProgress, setFormProgress] = useState<number>(0);

  const [disableProgress, setDisableProgress] = useState<boolean>(true);

  const [isFormSubmiting, setIsFormSubmiting] = useState<boolean>(false);

  // For now, we will show the form only when the option is selected otherwise it will be photo upload
  const [isTextBasedForm, setIsTextBasedForm] = useState<boolean>(false);

  const [isUploading, setIsUploading] = useState<boolean>(false);

  // ---------------------------- FUNCTIONS ---------------------------- //

  //TEMP - FOR FILE UPLOAD - UNTIL NEW DESIGN
  const handleClick = () => {
    inputRef.current?.click();
  };
  const handleStepChange = (step: number) => {
    setCurrentFormStep(FORM_STEP_ARRAY[step - 1] as FormSteps);
  };
  //TEMP - FOR FILE UPLOAD - UNTIL NEW DESIGN
  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    try {
      setIsUploading(true);
      if (file) {
        const { data, error } = await LewkAPI.lewkboards.upload.post({
          image: file,
        });

        await new Promise(resolve => setTimeout(resolve, 2000)); // wait for 2 seconds for file upload inconsistent uploads
        // @ts-ignore
        if (error) throw new NetworkError(error.value["error"], error.status);
        setIsUploading(false);
        setIsFormSubmiting(true);


        const { data: imageData, error: imageError } =
          await LewkAPI.lewkboards.generate.img.post({
            imgUrl: data.imageUrl,
          });

        if (imageError)
          // @ts-ignore
          throw new NetworkError(imageError.value["error"], imageError.status);
        router.push(
          `${Routes_Dashboard.LEWKBOARD_SUGGESTED}/${imageData.generationID}`
        );
      } else {
        throw new ClientError(
          "File was not detected",
          ClientStatusCodes.UNDOCUMENTED_ERROR
        );
      }
    } catch (error) {
      if (error instanceof NetworkError || error instanceof ClientError) {
        error.handle();
      } else {
        new ClientError(
          "Something went wrong with the file upload",
          ClientStatusCodes.UNDOCUMENTED_ERROR
        ).handle();
      }
    } finally {
      setIsUploading(false);
      setIsFormSubmiting(false);
    }
  };

  const onSubmit = async (formData: TGenerateLewkboardsRequest) => {
    setIsFormSubmiting(true);
    try {
      // Get user auth status -----TEMP FOR NOW
      const { data: auth_data, error: auth_error } =
        await supabase.auth.getUser();

      if (auth_error) {
        console.log("TEMP ERROR - IGNORE");
      }

      const { data, error } = await LewkAPI.lewkboards.generate.post(formData);

      // @ts-ignore
      if (error) throw new NetworkError(error.value["error"], error.status);

      //Increase usage count if the user is not logged in only after successful form submission
      if (auth_data.user === null) incrementUsageCount();

      router.push(
        `${Routes_Dashboard.LEWKBOARD_SUGGESTED}/${data.generationID}`
      );
    } catch (error) {
      setIsFormSubmiting(false);
      if (error instanceof NetworkError) {
        error.handle();
      } else {
        new ClientError(
          "We were not able to send your request. Please try again later.",
          ClientStatusCodes.UNDOCUMENTED_ERROR
        ).handle();
      }
    }
  };

  // ---------------------------- USE EFFECT ---------------------------- //
  useEffect(() => {
    const currentFormIndex =
      FORM_STEP_ARRAY.findIndex((value) => value == currentFormStep) + 1;

    setFormProgress(
      Math.round((currentFormIndex / FORM_STEP_ARRAY.length) * 100)
    );
  }, [currentFormStep]);

  return isFormSubmiting ? (
    <div className="size-full flex flex-col items-center justify-center">
      <Lottie className=" w-full" animationData={shopLoader} />
      <LewkLoader />
      <p>Generating....</p>
    </div>
  ) : (
    <FormProvider {...GlobalLewkboardForm}>
      <form
        onSubmit={GlobalLewkboardForm.handleSubmit(onSubmit)}
        className="size-full relative flex flex-col "
      >
        {isTextBasedForm ? (
          <div className="flex flex-col flex-1">
            {currentFormStep === FormSteps.OCCASION && (
              <FormOccassionStep
                handleStepChange={handleStepChange}
                disableProgress={setDisableProgress}
              />
            )}
            {currentFormStep === FormSteps.ITEM_TYPES && (
              <FormItemStep disableProgress={setDisableProgress} />
            )}
          </div>
        ) : (
          <div className="flex flex-col flex-1 items-center justify-center gap-8">
            <Label className=" text-center" variant={"multistep"}>
              What are you shopping for?
            </Label>
            <p>✨ Choose one</p>

            <button
              onClick={() => setIsTextBasedForm(true)}
              className="flex items-center bg-[#e9e9e9] rounded-2xl w-full group hover:bg-[#e2e2e2] transition-colors"
            >
              <div className="flex items-center gap-4 px-4 py-4">
                <GemIcon className="w-8 h-8" />
                <div className="w-px h-8 bg-black/10" />
                <span className="text-xl text-left">
                  Let us know the occasion and items you're looking for.
                </span>
              </div>
            </button>

            <button
              type="button"
              onClick={handleClick}
              className="flex items-center bg-[#e9e9e9] rounded-2xl w-full group hover:bg-[#e2e2e2] transition-colors"
            >
              <div className="flex items-center gap-4 px-4 py-4">
                <Camera className="w-8 h-8" />
                <div className="w-px h-8 bg-black/10" />
                <span className="text-xl text-left">
                  Upload an inspiration photo and we’ll help you recreate the
                  outfit.{" "}
                </span>
              </div>
              <input
                ref={inputRef}
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                onChange={handleFileChange}
                className="hidden"
              />
            </button>

            {isUploading && <p>File is Uploading...</p>}
          </div>
        )}
        <div className=" w-full h-16 flex items-end justify-between">
          <div className=" flex items-center h-8  gap-2">
            <CircleProgress className=" size-6" progress={formProgress} />
            <p className=" text-xs text-lewk-darkgrey">{`${FORM_STEP_ARRAY.findIndex((value) => value == currentFormStep) + 1}/${FORM_STEP_ARRAY.length}`}</p>
          </div>
          <div className="flex gap-2">
            {currentFormStep == FormSteps.ITEM_TYPES && (
              <Button
                type="button"
                onClick={() =>
                  handleStepChange(
                    FORM_STEP_ARRAY.findIndex(
                      (value) => value == currentFormStep
                    )
                  )
                }
                variant={"secondary"}
                size={"icon"}
              >
                <ChevronLeftIcon />
              </Button>
            )}
            {currentFormStep == FormSteps.OCCASION && (
              <Button
                type="button"
                disabled={disableProgress}
                onClick={() =>
                  handleStepChange(
                    FORM_STEP_ARRAY.findIndex(
                      (value) => value == currentFormStep
                    ) + 2
                  )
                }
                variant={"secondary"}
                size={"icon"}
              >
                <ChevronRightIcon />
              </Button>
            )}
            {currentFormStep == FormSteps.ITEM_TYPES && (
              <Button
                disabled={disableProgress}
                type="submit"
                variant={"primary"}
                size={"base"}
              >
                Submit
              </Button>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default LewkboardForm;
